import {useStore} from "vuex";

import {ref, computed} from "vue";
import {fetchStoryData} from "@songfinch/data-source/stories";
import {buildShareableUrl} from "@songfinch/utils/src/format";

import type {Story, OriginalStory, InstantStory} from "@songfinch/types";

const story = ref<Story>();

export default function useStory() {
    const store = useStore();

    type ShareModalPayload = {
        type: "story" | "post";
        url: string
        subjectText: string;
        text: string;
        title?: string;
        shareClip?: boolean;
        postId?: number;
    };

    const shareModalActive = ref<ShareModalPayload>();
    const openShareModal = (type: "story" | "post", postId?: number) => {
        const payload: ShareModalPayload = {
            type,
            url: window.location.href,
            subjectText: "Check out Songfinch!",
            text: `Click below to check out this story page on songfinch ${window.location.href}`,
        };

        if (type === "story") {
            const url = buildShareableUrl({
                utm_source: "songpage",
                utm_medium: "sharing",
                utm_campaign: "link",
                utm_id: "product",
            });

            payload.title = "Share this song";
            payload.shareClip = true;
            payload.url = url;
            payload.subjectText = "Check out this song on Songfinch!";
            payload.text = `Click below to listen to the personalized song I had created called ${storySong.value.title} by ${storySong.value?.artist?.fullName}.\nListen to the song here ${url}`;
        }

        if (type === "post" && postId) {
            const url = buildShareableUrl({
                id: postId?.toString(),
                utm_source: "songpage",
                utm_medium: "sharing",
                utm_campaign: "songpage-post",
                utm_id: "product",
            });

            payload.title = "Share this post";
            payload.postId = postId;
            payload.url = url;
            payload.subjectText = "Check out this post on Songfinch!";
            payload.text = `Click below to check out this post on songfinch ${window.location.href}`;
        }

        shareModalActive.value = payload;
    };

    const closeShareModal = () => {
        shareModalActive.value = undefined;
    };


    const isStoryOwner = computed(() => {
        return store.state.auth.user?.id === story.value?.owner_id;
    });

    const storySong = computed(() => {
        return (story.value as OriginalStory)?.song ?
            (story.value as OriginalStory).song :
            (story.value as InstantStory)?.instant_product;
    });

    const storySongTitle = computed(() => {
        // Check story progress
        return storySong.value?.title ? storySong.value?.title : "TBD Song Title";
    });

    async function showEmailCaptureModal(eventName: string) {
        const EmailCapturePopup = await import("@songfinch/customer/components/story/EmailCapturePopup.vue");

        store.commit("shared/showModal", {
            component: EmailCapturePopup.default,
            props: {
                story: story.value,
                eventName: eventName,
            },
            swalSettings: {
                width: 500,
                showCloseButton: false,
            },
            responsive: {
                1199: {
                    swalSettings: {
                        position: "bottom",
                        width: 347,
                    },
                },
            },
        });
    };

    async function loadCurrentStory(slug: string) {
        story.value = undefined;
        if (!slug) return false;
        try {
            const data = await fetchStoryData(slug);
            story.value = data;
            if (story.value.branded && !["american-greetings", "blue-mountain"].includes(story.value.affiliate as string)) {
                location.href = `/studios/${story.value.affiliate}/stories/${slug}`;
                return false;
            }
            return true;
        } catch (error) {
            // Do Nothing story_page.js will handle 404 and we don't want to log error to sentry
            return false;
        }
    };

    return {
        isStoryOwner,
        story,
        storySong,
        storySongTitle,
        shareModalActive,
        showEmailCaptureModal,
        loadCurrentStory,
        openShareModal,
        closeShareModal,
    };
}
